<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useUserSettingsStore } from '@/stores/user-settings';
import { useMutation, useQuery } from '@vue/apollo-composable';
import BottleneckModal from './BottleneckModal.vue';
import EscalationUpdateForm from './EscalationUpdateForm.vue';
import {
    type UpdateEscalationInput,
    type Bottleneck,
    type User,
    type Role,
} from '@/gql/graphql';
import { graphql } from '@/gql';
import type { UpdateEscalationFormValues } from './EscalationUpdateForm.vue';
import { useNotificationStore } from '@/stores/notification';
import { useDateTime } from '@/composables/datetime';

const { t } = useI18n({
    messages: {
        nl: {
            title: 'Escalatie aanpassen',
            update_success: 'Escalatie aangepast',
            update_error: 'Escalatie kon niet aangepast worden',
        },
        en: {
            title: 'Update escalation',
            update_success: 'Updated escalation',
            update_error: 'Error updating escalation',
        },
    },
});

const notificationStore = useNotificationStore();
const { activePortfolio } = useUserSettingsStore();
const { result: portfolioUsersResult } = useQuery(
    graphql(`
        query PortfolioloUsers($portfolioId: ID!) {
            users(portfolioId: $portfolioId) {
                items {
                    id
                    fullName
                    access {
                        roles {
                            name
                        }
                    }
                }
            }
        }
    `),
    {
        portfolioId: activePortfolio?.id || '',
    }
);

const { mutate: updateEscalation, loading: isUpdating } = useMutation(
    graphql(`
        mutation UpdateEscalation($input: UpdateEscalationInput!) {
            updateEscalation(input: $input) {
                result
            }
        }
    `),
    {
        refetchQueries: [
            'Deliverable',
            'ProjectPhases',
            'DeliverableBottlenecks',
            'Escalations',
            'ProgramProjects',
        ],
        awaitRefetchQueries: true,
    }
);

const escalationListUsers = computed<Pick<User, 'fullName' | 'id'>[]>(() => {
    const users = portfolioUsersResult.value?.users.items as User[];

    if (!users) return [];

    return users
        .filter((user) =>
            user?.access?.roles?.some(
                (role: Role) => role.name === 'Escalation list'
            )
        )
        .map((user) => ({ fullName: user.fullName, id: user.id }));
});
const props = defineProps<{
    bottleneck: Pick<Bottleneck, 'id' | 'users'> & {
        escalations: {
            escalationReason: string;
            expectedSolution: string | null;
            name: string | null;
            description: string | null;
            expectedResolutionDate: string | null;
        }[];
    };
}>();

const formValues = computed(() => ({
    name: props.bottleneck.escalations[0]?.name ?? '',
    description: props.bottleneck.escalations[0]?.description ?? '',
    expectedResolutionDate: props.bottleneck.escalations[0]?.expectedResolutionDate
        ? new Date(props.bottleneck.escalations[0].expectedResolutionDate)
        : null,
    assignedUser: props.bottleneck.users?.[0]?.id || undefined,
    solution: props.bottleneck.escalations[0]?.expectedSolution ?? '',
    escalationReason: props.bottleneck.escalations[0]?.escalationReason ?? '',
}));

const showDialog = defineModel<boolean>({ default: false });

async function handleSubmit(values: Required<UpdateEscalationFormValues>) {
    const { formattedDate } = useDateTime(values.expectedResolutionDate);
    const input: UpdateEscalationInput = {
        bottleneckId: props.bottleneck.id,
        name: values.name,
        description: values.description,
        expectedResolutionDate: formattedDate.value,
        escalationReason: values.escalationReason,
        solution: values.solution,
        assignUsers: [values.assignedUser],
        sendNotification: values.sendNotification,
    };

    try {
        await updateEscalation({ input });

        showDialog.value = false;
        notificationStore.add({
            type: 'success',
            message: t('update_success'),
        });
    } catch {
        notificationStore.add({ type: 'error', message: t('update_error') });
    }
}

function closeModal() {
    showDialog.value = false;
}
</script>

<template>
    <BottleneckModal v-model="showDialog" :title="t('title')">
        <template #activator="{ props: activatorProps }">
            <slot name="activator" :props="activatorProps" />
        </template>
        <EscalationUpdateForm
            :id="props.bottleneck.id"
            :initial-values="formValues"
            :disabled="isUpdating"
            :escalation-list="escalationListUsers"
            @submit="handleSubmit"
            @cancel="closeModal"
        />
    </BottleneckModal>
</template>
